import { useState, useMemo } from "react";
import { Plus, ArrowDownUp, Search, Download, FileText, Loader2, Eye } from "lucide-react";
import DataTable from "../../golive/components/DataTable/index.jsx";
import StatusBadge from "./history/StatusBadge.jsx";
import JobDetailsModal from "./history/JobDetailsModal.jsx";
import { useHistoryData, PAGE_SIZE } from "../hooks/useHistoryData.js";
import { formatDateTime } from "../utils/dateUtils.js";
import { downloadFileFromUrl } from "../utils/downloadUtil.js";

const HistoryTab = ({ onNewGeneration }) => {
  const {
    records, total, totalPages, loading,
    search, setSearch,
    sortOrder, setSortOrder,
    page, setPage,
  } = useHistoryData();

  const [downloadingId, setDownloadingId] = useState(null);
  const [selectedJob, setSelectedJob]     = useState(null);

  const handleDownload = async (record) => {
    if (!record.published_url || downloadingId) return;
    setDownloadingId(record.job_id);
    try {
      await downloadFileFromUrl(record.published_url);
    } catch {
      // silently ignore
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "sr_no",
        header: "Sr. No.",
        width: 70,
        align: "center",
        sortAllowed: false,
        cell: ({ row }) => (
          <span className="font-semibold text-slate-700">{row.index + 1}</span>
        ),
      },
      {
        accessorKey: "document_title",
        header: "Document Title",
        width: 200,
        sortAllowed: false,
        cell: ({ getValue }) => (
          <span className="font-medium text-slate-800 truncate block max-w-48" title={getValue()}>
            {getValue() || "-"}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        width: 120,
        sortAllowed: false,
        cell: ({ getValue }) => <StatusBadge status={getValue()} />,
      },
      {
        accessorKey: "created_at",
        header: "Generated At",
        width: 180,
        sortAllowed: false,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-slate-600">
            {formatDateTime(row.original.completed_at || row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        width: 120,
        align: "center",
        sortAllowed: false,
        cell: ({ row }) => {
          const record = row.original;
          return (
            <div className="flex items-center justify-center gap-1">
              <button
                type="button"
                onClick={() => setSelectedJob(record)}
                title="View details"
                className="p-1.5 rounded-lg border-0 bg-transparent text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
              >
                <Eye size={14} />
              </button>
              {record.status === "completed" && record.published_url && (
                <button
                  type="button"
                  onClick={() => handleDownload(record)}
                  disabled={!!downloadingId}
                  title="Download document"
                  className="p-1.5 rounded-lg border-0 bg-transparent text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {downloadingId === record.job_id
                    ? <Loader2 size={14} className="animate-spin" />
                    : <Download size={14} />}
                </button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [downloadingId]
  );

  const toolbar = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => setSortOrder((s) => (s === "desc" ? "asc" : "desc"))}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 transition-colors text-xs whitespace-nowrap cursor-pointer"
      >
        <ArrowDownUp size={13} />
        {sortOrder === "desc" ? "Newest first" : "Oldest first"}
      </button>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title or release tag…"
          className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-300 w-56"
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Document Generation History</h1>
            <p className="mt-0.5 text-sm text-slate-500">All document generations, newest first</p>
          </div>
          <button
            onClick={onNewGeneration}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#1d4ed8] text-white text-sm font-semibold border-0 cursor-pointer hover:bg-[#1e40af] transition-colors"
          >
            <Plus size={14} />
            New Document Generation
          </button>
        </div>

        {/* Table card */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-gray-100 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">Document Generations</span>
            {toolbar}
          </div>

          {loading ? (
            <div className="py-16 flex flex-col items-center gap-3 text-slate-400">
              <Loader2 size={28} className="animate-spin text-blue-400" />
              <p className="text-sm">Loading history…</p>
            </div>
          ) : records.length === 0 ? (
            <div className="py-16 flex flex-col items-center gap-3 text-slate-400">
              <FileText size={36} className="text-slate-300" />
              <p className="text-sm">
                {search
                  ? "No results match your search."
                  : "No document generations yet. Click New Document Generation to get started."}
              </p>
            </div>
          ) : (
            <>
              <DataTable
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
                table={{ columns, rows: records }}
              />
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
                  <p className="text-xs text-slate-500">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-slate-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-slate-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <JobDetailsModal
        job={selectedJob}
        onClose={() => setSelectedJob(null)}
        downloadingId={downloadingId}
        onDownload={handleDownload}
      />
    </>
  );
};

export default HistoryTab;
